import api from './api';

export interface Track {
  trackId: string;
  name: string;
  sku: string;
  costPrice: number;
  salesPrice: number;
  description: string;
  assignmentType: 'RANDOM' | 'ORDERED' | 'MANDATORY';
  createdDate: string;
  timestamp: string;
  courses?: Course[];
}

export interface Course {
  courseId: string;
  lmsPlatformId: string;
  title: string;
  description?: string;
  syncedDate: string;
}

/**
 * Service for fetching track data from the API
 */
export const trackService = {
  /**
   * Get all available courses
   * @returns Promise with the list of all courses
   */
  getAllCourses: async (): Promise<Course[]> => {
    try {
      const response = await api.get('/course');
      
      // Check if the response has the expected structure
      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (response.data && typeof response.data === 'object' && response.data.data) {
        // If data is not an array but an object with data property
        if (typeof response.data.data === 'object') {
          return Object.values(response.data.data);
        }
      }
      
      // Fallback to empty array if data structure is unexpected
      console.warn('Unexpected API response structure:', response.data);
      return [];
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw error;
    }
  },
  /**
   * Get all tracks
   * @returns Promise with the list of tracks
   */
  getTracks: async (): Promise<Track[]> => {
    try {
      const response = await api.get('/track');
      
      // Check if the response has the expected structure
      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (response.data && typeof response.data === 'object' && response.data.data) {
        // If data is not an array but an object with data property
        if (typeof response.data.data === 'object') {
          return Object.values(response.data.data);
        }
      }
      
      // Fallback to empty array if data structure is unexpected
      console.warn('Unexpected API response structure:', response.data);
      return [];
    } catch (error) {
      console.error('Error fetching tracks:', error);
      throw error;
    }
  },

  /**
   * Get a specific track by ID
   * @param trackId The ID of the track to get
   * @returns Promise with the track data
   */
  getTrack: async (trackId: string): Promise<Track> => {
    try {
      const response = await api.get(`/track/${trackId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching track ${trackId}:`, error);
      throw error;
    }
  },

  /**
   * Get courses for a specific track
   * @param trackId The ID of the track to get courses for
   * @returns Promise with the list of courses
   */
  getTrackCourses: async (trackId: string): Promise<Course[]> => {
    try {
      // First try to get the track details, which may include courses
      const track = await trackService.getTrack(trackId);
      
      // If the track has courses, return them
      if (track.courses && Array.isArray(track.courses)) {
        return track.courses;
      }
      
      // If the track doesn't have courses, try to get them separately
      const response = await api.get(`/track/${trackId}/courses`);
      
      // Check if the response has the expected structure
      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (response.data && typeof response.data === 'object' && response.data.data) {
        // If data is not an array but an object with data property
        if (typeof response.data.data === 'object') {
          return Object.values(response.data.data);
        }
      }
      
      // Fallback to empty array if data structure is unexpected
      console.warn('Unexpected API response structure:', response.data);
      return [];
    } catch (error) {
      console.error(`Error fetching courses for track ${trackId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new track
   * @param track The track data to create
   * @returns Promise with the created track
   */
  createTrack: async (track: Omit<Track, 'trackId'>): Promise<Track> => {
    try {
      const response = await api.post('/track', track);
      return response.data.data;
    } catch (error) {
      console.error('Error creating track:', error);
      throw error;
    }
  },

  /**
   * Update an existing track
   * @param trackId The ID of the track to update
   * @param track The updated track data
   * @returns Promise with the updated track
   */
  updateTrack: async (trackId: string, track: Partial<Omit<Track, 'trackId'>>): Promise<Track> => {
    try {
      const response = await api.put(`/track/${trackId}`, track);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating track ${trackId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a track
   * @param trackId The ID of the track to delete
   * @returns Promise with the result of the operation
   */
  deleteTrack: async (trackId: string): Promise<void> => {
    try {
      await api.delete(`/track/${trackId}`);
    } catch (error) {
      console.error(`Error deleting track ${trackId}:`, error);
      throw error;
    }
  },

  /**
   * Add courses to a track
   * @param trackId The ID of the track to add courses to
   * @param courseIds Array of course IDs to add
   * @returns Promise with the result of the operation
   */
  addCoursesToTrack: async (trackId: string, courseIds: string[]): Promise<void> => {
    try {
      await api.post(`/track/${trackId}/courses`, { courseIds });
    } catch (error) {
      console.error(`Error adding courses to track ${trackId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a course from a track
   * @param trackId The ID of the track to remove the course from
   * @param courseId The ID of the course to remove
   * @returns Promise with the result of the operation
   */
  removeCourseFromTrack: async (trackId: string, courseId: string): Promise<void> => {
    try {
      await api.delete(`/track/${trackId}/courses/${courseId}`);
    } catch (error) {
      console.error(`Error removing course ${courseId} from track ${trackId}:`, error);
      throw error;
    }
  }
};

export default trackService;
